import React, { useEffect, useState } from "react";
import { Plus, Tag } from "lucide-react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import TextInput from "presentation/components/UI/TextInput";
import { Button } from "presentation/components/UI/Button";
import { orderRecognitionRuleUseCase } from "../../../../di/container";
import RuleBlockCard, { type RuleBlockVariant } from "./RuleBlockCard";

interface KeywordForm {
  keyword: string;
}

interface OrderKeywordsBlockProps {
  onNotify: (message: string) => void;
  variant?: RuleBlockVariant;
}

/** 주문 키워드 블럭: 메일 내용에 키워드가 있으면 주문으로 인식 */
const OrderKeywordsBlock: React.FC<OrderKeywordsBlockProps> = ({
  onNotify,
  variant = "card",
}) => {
  const { companyId } = useParams();
  const [keywords, setKeywords] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const keywordForm = useForm<KeywordForm>({
    defaultValues: { keyword: "" },
  });

  // 주문 키워드 목록을 불러온다
  useEffect(() => {
    const cid = Number(companyId);
    if (!Number.isFinite(cid)) return;

    const fetchRule = async () => {
      try {
        const rule = await orderRecognitionRuleUseCase.getRule(cid);
        setKeywords(rule.keywords);
      } catch (error) {
        console.error("Error fetching order keywords:", error);
      }
    };

    fetchRule();
  }, [companyId]);

  // 키워드 목록을 백엔드로 일괄 저장하고 성공 시 로컬 상태를 갱신
  const persistKeywords = async (
    cid: number,
    nextKeywords: string[],
  ): Promise<boolean> => {
    try {
      const rule = await orderRecognitionRuleUseCase.saveRule(cid, {
        keywords: nextKeywords,
      });
      setKeywords(rule.keywords);
      return true;
    } catch (error) {
      console.error("Error saving order keywords:", error);
      return false;
    }
  };

  const handleAddKeyword = async (data: KeywordForm) => {
    const cid = Number(companyId);
    if (!Number.isFinite(cid)) return;

    const keyword = data.keyword.trim();
    if (!keyword) return;

    if (keywords.includes(keyword)) {
      onNotify("이미 등록된 키워드입니다.");
      return;
    }

    setIsSaving(true);
    const ok = await persistKeywords(cid, [...keywords, keyword]);
    setIsSaving(false);

    if (ok) {
      keywordForm.reset({ keyword: "" });
      onNotify("주문 키워드가 추가되었습니다.");
    } else {
      onNotify("주문 키워드 추가에 실패했습니다.");
    }
  };

  const handleDeleteKeyword = async (keyword: string) => {
    const cid = Number(companyId);
    if (!Number.isFinite(cid)) return;

    setIsSaving(true);
    const ok = await persistKeywords(
      cid,
      keywords.filter((k) => k !== keyword),
    );
    setIsSaving(false);

    if (ok) {
      onNotify("주문 키워드가 삭제되었습니다.");
    } else {
      onNotify("주문 키워드 삭제에 실패했습니다.");
    }
  };

  return (
    <RuleBlockCard
      variant={variant}
      icon={<Tag className="text-[#F69E09]" />}
      iconBgClassName="bg-[#FFF7E6]"
      title="주문 키워드"
      description="메일 내용에 아래 단어 중 하나라도 있으면 주문으로 가져옵니다. 자유롭게 추가/삭제하세요."
    >
      <form
        className="text-start flex flex-row gap-2 w-full"
        onSubmit={keywordForm.handleSubmit(handleAddKeyword)}
      >
        <TextInput
          className="w-full"
          placeholder="주문 키워드를 입력하세요"
          error={
            keywordForm.formState.errors.keyword
              ? {
                  message: keywordForm.formState.errors.keyword.message ?? "",
                  isMessageVisible: true,
                  isIconVisible: true,
                }
              : undefined
          }
          {...keywordForm.register("keyword", {
            required: "필수 항목이에요.",
            validate: (value) =>
              value.trim().length > 0 || "필수 항목이에요.",
          })}
        />
        <Button
          className="w-full max-w-[120px] max-h-[46px]"
          variant="primary"
          type="submit"
          disabled={isSaving}
        >
          키워드 추가
        </Button>
      </form>

      {keywords.length === 0 && (
        <div className="mt-4 text-[13px] text-gray-400">
          등록된 주문 키워드가 없습니다.
        </div>
      )}

      <div
        className={`${keywords.length > 0 ? "mt-4" : ""} flex flex-row flex-wrap gap-2 gap-y-3`}
      >
        {keywords.map((keyword) => (
          <button
            key={keyword}
            type="button"
            onClick={() => handleDeleteKeyword(keyword)}
            disabled={isSaving}
            className="bg-[#FFF7E6] outline outline-[#F69E09] rounded-full p-1 flex flex-row items-center w-min disabled:opacity-50"
          >
            <span className="mr-3 text-[14px] w-max ml-2 mr-1 whitespace-nowrap">
              {keyword}
            </span>
            <span className="flex shrink-0 items-center justify-center w-5 h-5 rounded-full bg-[#F69E09]">
              <Plus className="rotate-45 text-white" size={14} />
            </span>
          </button>
        ))}
      </div>
    </RuleBlockCard>
  );
};

export default OrderKeywordsBlock;
